import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { PageLightbox } from './PageLightbox';
import { lookInsidePages, type LookInsidePage } from '../data/lookInsidePages';

/**
 * Branded stand-in used while a page has no image (src empty).
 * Replace each src in data/lookInsidePages.ts with the real Volume 1 render.
 */
function PagePlaceholder({
  page
}: {
  page: LookInsidePage;
}) {
  const rows = [{
    dots: 1,
    color: 'bg-sage',
    name: 'Starter'
  }, {
    dots: 2,
    color: 'bg-smoke',
    name: 'Core'
  }, {
    dots: 3,
    color: 'bg-lilac',
    name: 'Stretch'
  }];
  return <div className="flex aspect-[9/16] w-full flex-col bg-cream text-left" data-id="element-135" data-mp="1my4jg76z82le">
      <div className="bg-plum px-5 pb-5 pt-6" data-id="element-136" data-mp="1mw1uzrlh2his">
        <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-137" data-mp="18y3ikrqpov5x">
          {page.label}
        </span>
        <span className="mt-2 block font-display text-lg font-bold leading-tight text-white" data-id="element-138" data-mp="1dwn4is5b26ya">
          {page.caption}
        </span>
      </div>
      <span className="h-2 w-full bg-coral" data-id="element-139" data-mp="61clh3zk6ehn" />
      <div className="flex flex-1 flex-col gap-3 px-5 py-5" data-id="element-140" data-mp="60jcrxh1hewv">
        {rows.map(row => <div key={row.name} className="rounded-xl border border-cream-dark bg-white px-3 py-2.5" data-id="element-141" data-mp="jzj4jdzum9x9">
            <div className="flex items-center gap-1.5" data-id="element-142" data-mp="ezy40z515tif">
              {Array.from({
            length: row.dots
          }).map((_, i) => <span key={i} className={`h-2 w-2 rounded-full ${row.color}`} data-id="element-143" data-mp="3iir9sprkhe21" data-mpi={i} />)}
              <span className="ml-1 text-[10px] font-semibold uppercase tracking-widest text-ink-soft" data-id="element-144" data-mp="11q0279ktrxr">
                {row.name}
              </span>
            </div>
            <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-ink" data-id="element-145" data-mp="3seknwdkxpflx">
              1 &amp; 2 &amp; 3 &amp; 4 &amp; 5 &amp; 6 &amp; 7 &amp; 8
            </p>
          </div>)}
        <div className="mt-auto rounded-xl border-2 border-dashed border-gold bg-gold-soft px-3 py-2.5" data-id="element-146" data-mp="2zpvu6zm7kck7">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-gold-deep" data-id="element-147" data-mp="13mr0fdbmlk70">
            Make It Yours
          </span>
        </div>
      </div>
    </div>;
}
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const pagesWithImages = lookInsidePages.filter(p => p.src);
  const getStep = () => {
    const track = trackRef.current;
    const first = track?.children[0] as HTMLElement | undefined;
    if (!track || !first) return 0;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.offsetWidth + gap;
  };
  const updateState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const step = getStep();
    const maxScroll = track.scrollWidth - track.clientWidth;
    setAtStart(track.scrollLeft <= 4);
    setAtEnd(track.scrollLeft >= maxScroll - 4);
    if (step > 0) {
      const idx = track.scrollLeft >= maxScroll - 4 ? lookInsidePages.length - 1 : Math.round(track.scrollLeft / step);
      setActive(Math.min(idx, lookInsidePages.length - 1));
    }
  }, []);
  useEffect(() => {
    updateState();
    window.addEventListener('resize', updateState);
    return () => window.removeEventListener('resize', updateState);
  }, [updateState]);
  const scrollByCards = (dir: 1 | -1) => {
    trackRef.current?.scrollBy({
      left: dir * getStep(),
      behavior: 'smooth'
    });
  };
  const scrollToIndex = (i: number) => {
    trackRef.current?.scrollTo({
      left: i * getStep(),
      behavior: 'smooth'
    });
  };
  const openPage = (page: LookInsidePage) => {
    const idx = pagesWithImages.indexOf(page);
    if (idx >= 0) setLightboxIndex(idx);
  };
  return <section aria-labelledby="look-inside-heading" className="w-full bg-cream text-ink" data-id="element-148" data-mp="3hxe3lrbp7nlc">
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20" data-id="element-149" data-mp="3oxq3iu3emt2x">
        <div className="text-center" data-id="element-150" data-mp="3txujmj0ehlsm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-coral-deep" data-id="element-151" data-mp="3twt7scbk8v9u">
            Look inside
          </p>
          <h2 id="look-inside-heading" className="mt-3 font-display font-bold text-plum text-3xl sm:text-5xl" data-id="element-152" data-mp="3k06qfxig0mck">
            Open the week. Pick 30, 45 or 60. Teach.
          </h2>
          <p className="mx-auto mt-4 max-w-[60ch] text-base text-ink" data-id="element-153" data-mp="3b0be3pd5ilay">
            Pages from Volume 1: every combo written in 8-counts, in three
            levels, with BPM, cues and a game to close the class.
          </p>
        </div>

        <div className="relative mt-10" data-id="element-154" data-mp="31ai3aqku2a7r">
          <ul ref={trackRef} onScroll={updateState} className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-5 px-5 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:gap-6 md:scroll-px-0 md:px-0" data-id="element-155" data-mp="2n9xgzkfplw9i">
            {lookInsidePages.map(page => <li key={page.label} className="w-[80%] shrink-0 snap-start md:w-[calc((100%-3rem)/3)]" data-id="element-156" data-mp="23pyvye16vrkk">
                <figure data-id="element-157" data-mp="2dmuxu0wl1fg5">
                  {page.src ? <button type="button" onClick={() => openPage(page)} aria-label={`Open full size: ${page.caption}`} className="block w-full overflow-hidden rounded-2xl border border-cream-dark bg-white shadow-card transition-transform duration-200 ease-out hover:-translate-y-0.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50" data-id="element-158" data-mp="2dla3aba9pwia">
                      <img src={page.src} alt={page.caption} loading="lazy" width={900} height={1600} className="aspect-[9/16] w-full object-contain" data-id="element-159" data-mp="3accaiztzg1rc" />
                    </button> : <div className="overflow-hidden rounded-2xl border border-cream-dark shadow-card" data-id="element-160" data-mp="2c1b38zwv9rb5">
                      <PagePlaceholder page={page} data-id="element-161" data-mp="2grzuce4mwm9r" />
                    </div>}
                  <figcaption className="mt-3 text-center text-sm text-ink-soft" data-id="element-162" data-mp="2lr2ylwcsiogo">
                    {page.caption}
                  </figcaption>
                </figure>
              </li>)}
          </ul>

          <button type="button" onClick={() => scrollByCards(-1)} disabled={atStart} aria-label="Previous pages" className="absolute -left-5 top-[calc(50%-1.5rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-plum text-white shadow-card transition-[background-color,opacity] duration-150 hover:bg-plum-mid focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 disabled:pointer-events-none disabled:opacity-30 md:flex lg:-left-6" data-id="element-163" data-mp="2voqx8xdiog44">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-164" data-mp="t1aeu421sl3s" />
          </button>
          <button type="button" onClick={() => scrollByCards(1)} disabled={atEnd} aria-label="Next pages" className="absolute -right-5 top-[calc(50%-1.5rem)] hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-plum text-white shadow-card transition-[background-color,opacity] duration-150 hover:bg-plum-mid focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 disabled:pointer-events-none disabled:opacity-30 md:flex lg:-right-6" data-id="element-165" data-mp="o41v3z19490f">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-166" data-mp="3shqm9rc63a0n" />
          </button>
        </div>

        <div className="mt-4 flex justify-center gap-2" role="tablist" aria-label="Choose page" data-id="element-167" data-mp="3nix6fs9er6xb">
          {lookInsidePages.map((page, i) => <button key={page.label} type="button" role="tab" aria-selected={active === i} aria-label={`Go to page ${i + 1}: ${page.caption}`} onClick={() => scrollToIndex(i)} className="flex h-6 w-6 items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-full" data-id="element-168" data-mp="1repsegw0dad6" data-mpi={i}>
              <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${active === i ? 'w-6 bg-plum' : 'w-2 bg-cream-dark'}`} data-id="element-169" data-mp="2a6s8nr4qmomw" />
            </button>)}
        </div>

        <div className="mt-10 flex flex-col items-center" data-id="element-170" data-mp="25dpephw1td66">
          <p className="text-sm text-ink-soft" data-id="element-171" data-mp="2t4cpqc19giwy">
            6 volumes · 40 weeks · one-time $27
          </p>
          <div className="mt-4 w-full max-w-md" data-id="element-172" data-mp="2xvupwxvgmfgv">
            <CtaButton href="#pricing" data-id="element-173" data-mp="2xxo0go971ju5">Get all 40 weeks</CtaButton>
          </div>
        </div>
      </div>

      <PageLightbox pages={pagesWithImages} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onChange={setLightboxIndex} data-id="element-174" data-mp="me9bz7twwkuo" />
    </section>;
}